/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * auth-client.ts
 *
 * TA-2 (S576): this was a Better Auth client built with createAuthClient from
 * better-auth/react. Better Auth never reached production -- it ran on the test
 * staging environment only -- so every session lookup went to a service that is
 * not there, and the customer appeared logged out however many times they signed
 * in on the website.
 *
 * The session is now the JWT the shop stores under ds_auth_token. It arrives
 * either in the address bar (?authToken=, after signing in on the website) or in
 * the checkout response (initial_jwt, after paying). There is no network call.
 *
 * The exported surface is deliberately unchanged -- useSession, getSession and
 * signOut -- so the eleven call sites keep working.
 */

import { useCallback, useEffect, useState } from "react";

import {
  AUTH_RENEWAL_EVENT,
  AUTH_TOKEN_EVENT,
  clearAuthToken,
  decodeJwtPayload,
  getStoredAuthToken,
  revokeStoredRefreshToken,
  tokenCache,
} from "@/lib/token-cache";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
}

export interface AuthSession {
  user: AuthUser;
}

/**
 * Build a session object from the stored token.
 * Returns null when there is no token or the token has expired --
 * getStoredAuthToken removes an expired token rather than handing back
 * something that will be rejected.
 */
function readSession(): AuthSession | null {
  const token = getStoredAuthToken();
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  const email = String(payload.email ?? "");
  const id = String(payload.sub ?? payload.user_id ?? "");
  if (!email && !id) return null;

  return {
    user: {
      id,
      email,
      name: String(payload.name ?? email.split("@")[0] ?? ""),
      // FCR S771 (#39): the website's tokens carry `emailVerified` (camel case),
      // as the shop's back end already reads it (authenticate.py). The old read
      // of `email_verified`, a name the website never sends, defaulted to TRUE,
      // so every session looked proven. An account the shop creates at checkout
      // stays unverified until the customer proves the address; only an explicit
      // `true` is a verified session -- false, missing or any other value is not.
      emailVerified: payload.emailVerified === true,
    },
  };
}

/**
 * React hook mirroring the shape the call sites already expect.
 *
 * isPending is true only for the first synchronous read. Reading localStorage
 * is instantaneous, so a long pending state would be a lie -- and a guard that
 * waits on it would flash the sign-in page at a signed-in customer.
 */
function useSession(): { data: AuthSession | null; isPending: boolean } {
  const [data, setData] = useState<AuthSession | null>(() => readSession());
  // Requirement 3 / 7 (FCR S674): the guards read BOTH tokens.
  //
  // Between the access token expiring and the renewed one arriving there is no
  // session to read, and a guard that decides from that alone sends a signed-in
  // customer to the sign-in page -- for an instant on every visit after an hour
  // away. So "pending" means: there is no readable session right now, and there
  // is a refresh token, or a renewal is actually in flight. In both cases the
  // honest answer is "wait", not "signed out".
  const readPending = useCallback(
    (session: AuthSession | null) => !session && tokenCache.shouldHoldForRenewal(),
    [],
  );
  const [isPending, setIsPending] = useState(() => readPending(readSession()));

  const refresh = useCallback(() => {
    const session = readSession();
    setData(session);
    const pending = readPending(session);
    setIsPending(pending);
    // Start the attempt the hold is waiting for. Without this the guards would
    // wait for something nobody had begun.
    if (pending) tokenCache.beginRenewalIfNeeded();
  }, [readPending]);

  useEffect(() => {
    // ds-auth-updated fires in THIS tab when a token is stored or cleared.
    // storage fires in OTHER tabs. Listening to both is what makes signing out
    // in one tab sign the customer out in all of them.
    // ds-auth-renewal fires when a renewal starts or finishes.
    window.addEventListener(AUTH_TOKEN_EVENT, refresh);
    window.addEventListener(AUTH_RENEWAL_EVENT, refresh);
    window.addEventListener("storage", refresh);
    refresh();
    return () => {
      window.removeEventListener(AUTH_TOKEN_EVENT, refresh);
      window.removeEventListener(AUTH_RENEWAL_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [refresh]);

  return { data, isPending };
}

async function getSession(): Promise<{ data: AuthSession | null }> {
  return { data: readSession() };
}

/**
 * End the session -- at the service as well as in this browser.
 *
 * This signs the customer out of the shop. Signing them out of the website as
 * well is a separate redirect -- see redirectToDeskshareLogout in auth-redirect.ts
 * -- which every customer-facing sign-out (dashboard and cart header) follows
 * this with (FCR S771, #42).
 *
 * Item 8 (FCR S687): the revoke request goes FIRST, before anything clears
 * storage. Clearing first and revoking second revokes nothing -- the ordering is
 * the whole fix. Until this, clearAuthToken() deleted the refresh token from the
 * browser and left it valid on the server for up to thirty days after the
 * customer believed they had signed out; on a shared or public machine that is
 * the case that matters most, and it is one of the two protections that make the
 * thirty-day lifetime (Decision 1) the right choice rather than the wrong one.
 *
 * Correction 3 (item 8): this function is now genuinely asynchronous -- it was
 * already declared `async`, but it had nothing to await. Both call sites
 * (main-nav.tsx and app-sidebar.tsx) already chain their cleanup off `.then()`,
 * so the revoke completes before the page reloads or navigates. A caller that
 * navigated without waiting would have the browser cancel the request in flight,
 * and the fix would appear to be in place and do nothing.
 */
async function signOut(): Promise<{ error: null }> {
  await revokeStoredRefreshToken();
  clearAuthToken();
  return { error: null };
}

export const authClient = {
  useSession,
  getSession,
  signOut,
};

export default authClient;
